import axios from 'axios'
import {useEffect, useState} from 'react'
import {API_URL} from '../../../../../_metronic/helpers'
import {UserCompanyModel} from '../../../auth/core/_models'
import {BusinessOptionData} from './BusinessOption'

// What the business picker shows for one business, decided by the server: the CRM trade name
// when there is one, and the economic code (the national code when there is none). Businesses of
// one civil partnership share a national code, so the national code alone cannot tell them apart.
// The label is the one the business's users gave it in the panel (backend 1.0.0.587+); the taxpayer
// type and the economic code alone come from backend 1.0.0.638, the tax branch code from 1.0.0.688.
export type BusinessSwitcherLabel = {
  id: number
  name: string
  code: string
  label?: string | null
  type?: number | null
  economicCode?: string | null
  branchCode?: string | null
}

export type BusinessSwitcherLabels = Record<number, BusinessSwitcherLabel>

const BUSINESS_SWITCHER_LABELS_URL = `${API_URL}/taxpayers/switcher-labels`

let cachedKey = ''
let cachedRequest: Promise<BusinessSwitcherLabels> | null = null

export const businessOptionText = (
  company: Pick<UserCompanyModel, 'nm' | 'nid'>,
  label?: BusinessSwitcherLabel
) => {
  const name = label?.name?.trim() || company.nm?.trim() || ''
  const code = label?.code?.trim() || company.nid?.trim() || ''
  const panelLabel = label?.label?.trim()
  const branch = label?.branchCode?.trim()
  const named = panelLabel ? `${name} – ${panelLabel}` : name
  const branched = branch ? `${named} شعبه ${branch}` : named
  return code ? `${branched} ${code}` : branched
}

/**
 * The two-line picker's parts for one of the session's businesses: the server's label when it came,
 * otherwise the session's own name and national code.
 */
export const businessOptionData = (
  company: Pick<UserCompanyModel, 'id' | 'nm' | 'nid'>,
  label?: BusinessSwitcherLabel
): BusinessOptionData =>
  label
    ? {
        id: company.id,
        name: label.name?.trim() || company.nm?.trim() || '',
        label: label.label,
        type: label.type,
        economicCode: label.economicCode,
        code: label.code?.trim() || company.nid?.trim() || '',
        branchCode: label.branchCode,
      }
    : {id: company.id, name: company.nm?.trim() || '', economicCode: null, code: company.nid?.trim() || ''}

const readType = (value: unknown) =>
  typeof value === 'number' && value >= 1 && value <= 4 ? value : value === undefined ? undefined : null

const toLabels = (rows: unknown): BusinessSwitcherLabels =>
  (Array.isArray(rows) ? rows : []).reduce<BusinessSwitcherLabels>((labels, row) => {
    if (row && typeof row.id === 'number') {
      labels[row.id] = {
        id: row.id,
        name: String(row.name ?? ''),
        code: String(row.code ?? ''),
        label: typeof row.label === 'string' ? row.label : null,
        type: readType(row.type),
        // Absent (undefined) on a backend before 1.0.0.638, null when the business has none.
        economicCode:
          typeof row.economicCode === 'string' ? row.economicCode : 'economicCode' in row ? null : undefined,
        branchCode:
          typeof row.branchCode === 'string' && row.branchCode.trim() ? row.branchCode.trim() : null,
      }
    }
    return labels
  }, {})

// One request per set of businesses: both pickers on a page share it, and choosing another
// business reloads the page anyway.
const loadBusinessSwitcherLabels = (key: string) => {
  if (!cachedRequest || cachedKey !== key) {
    cachedKey = key
    cachedRequest = axios.get(BUSINESS_SWITCHER_LABELS_URL).then((response) => toLabels(response.data))
    cachedRequest.catch(() => {
      // Let the next picker ask again rather than keep a failed answer for the whole visit.
      if (cachedKey === key) cachedRequest = null
    })
  }
  return cachedRequest
}

export const resetBusinessSwitcherLabels = () => {
  cachedKey = ''
  cachedRequest = null
}

// The labels the server knows for the session's businesses. Until they arrive, or when the server
// cannot give them, the picker keeps the session's own name and national code.
export const useBusinessSwitcherLabels = (companies?: Pick<UserCompanyModel, 'id'>[]) => {
  const key = (companies || []).map((company) => company.id).join(',')
  const [labels, setLabels] = useState<BusinessSwitcherLabels>({})

  useEffect(() => {
    if (!key) return
    let active = true
    loadBusinessSwitcherLabels(key).then(
      (loaded) => {
        if (active) setLabels(loaded)
      },
      () => {
        // The session's own names stay on screen; the picker still works without the labels.
      }
    )
    return () => {
      active = false
    }
  }, [key])

  return labels
}
